/** Zephyrwrite Cloud API client + desktop magic-link session. */

import * as Sentry from "@sentry/vue";
import {
  readStringPref,
  removePref,
  writeJsonPref,
  readJsonPref,
} from "./localPrefs";
import { cloudApiBaseUrl } from "./cloudApiBase";
import { decodeEntitlementsResponse } from "./cloud/entitlementsDecode";
import { matchMaybe } from "./maybe";
import { isTauri } from "./runtime";
import { trimmedOrEmpty } from "./trimmedString";

export type CloudEntitlement = {
  kind: string;
  status: string;
  active: boolean;
  currentPeriodEnd?: string | null;
};

export type CloudSession = {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresIn: number;
  email: string;
  userId: string;
  savedAt: number;
};

export type EntitlementsResponse = {
  entitlements: CloudEntitlement[];
  issuedAt: string;
  graceUntil: string;
};

function apiErrorMessage(body: unknown, fallback: string): string {
  if (typeof body === "object" && body !== null && "error" in body) {
    const err = (body as { error?: unknown }).error;
    if (typeof err === "string" && err.length > 0) return err;
  }
  return fallback;
}

export function getCloudSession(): CloudSession | null {
  return matchMaybe(readStringPref("cloudSession"), {
    nothing: () => null,
    just: (raw) => {
      try {
        return JSON.parse(raw) as CloudSession;
      } catch {
        return null;
      }
    },
  });
}

export function clearCloudSession(): void {
  removePref("cloudSession");
}

export function saveCloudSession(
  session: Omit<CloudSession, "savedAt">,
): CloudSession {
  const full: CloudSession = { ...session, savedAt: Date.now() };
  writeJsonPref("cloudSession", full);
  return full;
}

export function parseAuthCallbackUrl(url: string): CloudSession | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "zephyrwrite:") return null;

    const isCallback =
      (parsed.host === "auth" && parsed.pathname.startsWith("/callback")) ||
      parsed.pathname.includes("/auth/callback");
    if (isCallback === false) return null;

    const accessToken = parsed.searchParams.get("access_token");
    const refreshToken = parsed.searchParams.get("refresh_token");
    const email = parsed.searchParams.get("email");
    const userId = parsed.searchParams.get("user_id");
    const expires = parsed.searchParams.get("access_token_expires_in");

    if (
      accessToken === null ||
      accessToken.length === 0 ||
      refreshToken === null ||
      refreshToken.length === 0 ||
      email === null ||
      email.length === 0 ||
      userId === null ||
      userId.length === 0
    ) {
      return null;
    }

    return saveCloudSession({
      accessToken,
      refreshToken,
      email,
      userId,
      accessTokenExpiresIn:
        expires !== null && expires.length > 0 ? Number(expires) : 3600,
    });
  } catch {
    return null;
  }
}

export async function requestDesktopMagicLink(
  email: string,
): Promise<CloudSession> {
  const platform =
    typeof navigator !== "undefined" ? trimmedOrEmpty(navigator.platform) : "";
  const deviceName = platform.length > 0 ? platform : "Desktop";
  const handoffId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `zw_${Date.now()}_${Math.random().toString(36).slice(2)}`;

  const url = `${cloudApiBaseUrl()}/v1/auth/magic-link`;

  const res = await fetchResponse(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      email,
      client: "desktop",
      device_name: `Zephyrwrite (${deviceName})`,
      handoff_id: handoffId,
    }),
  });
  if (res === null) {
    console.error(`Zephyrwrite Cloud unreachable at ${cloudApiBaseUrl()}`);
    throw new Error(
      "Couldn't reach Zephyrwrite Cloud. Check your connection and try again.",
    );
  }

  if (res.ok === false) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      apiErrorMessage(body, `Sign-in failed (${res.status}). Try again.`),
    );
  }

  // Poll until the writer confirms the email link in the browser.
  // Custom schemes (zephyrwrite://) do not work under `tauri dev` on macOS.
  return pollDesktopHandoff(handoffId);
}

async function pollDesktopHandoff(
  handoffId: string,
  opts: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<CloudSession> {
  const timeoutMs = opts.timeoutMs ?? 15 * 60 * 1000;
  const intervalMs = opts.intervalMs ?? 1500;
  const started = Date.now();

  while (Date.now() - started < timeoutMs) {
    const res = await fetchResponse(
      `${cloudApiBaseUrl()}/v1/auth/handoff/${encodeURIComponent(handoffId)}`,
      {},
    );
    if (res === null) {
      await sleep(intervalMs);
      continue;
    }

    if (res.status === 404) {
      await sleep(intervalMs);
      continue;
    }

    if (res.status === 410) {
      throw new Error("Sign-in timed out. Send yourself a new link.");
    }

    const body = await res.json().catch(() => ({}));

    if (body.status === "ready") {
      return saveCloudSession({
        accessToken: body.access_token,
        refreshToken: body.refresh_token,
        accessTokenExpiresIn: body.access_token_expires_in ?? 3600,
        email: body.email,
        userId: body.user_id,
      });
    }

    if (body.status === "pending") {
      await sleep(intervalMs);
      continue;
    }

    await sleep(intervalMs);
  }

  throw new Error("Sign-in timed out. Send yourself a new link.");
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Fetch, or null when the network itself fails. Callers still check status. */
async function fetchResponse(
  url: string,
  init: RequestInit,
): Promise<Response | null> {
  try {
    return await fetch(url, init);
  } catch {
    return null;
  }
}

/**
 * Access tokens last an hour. Refresh a minute early so the burst of cloud
 * calls on app start does not each take a 401 detour through the refresh
 * endpoint (which rotates the token and invalidates the others).
 */
const ACCESS_TOKEN_SKEW_MS = 60 * 1000;

function accessTokenStale(session: CloudSession): boolean {
  const ttlMs = (session.accessTokenExpiresIn ?? 0) * 1000;
  if (ttlMs === 0 || session.savedAt === 0) return false;
  return Date.now() >= session.savedAt + ttlMs - ACCESS_TOKEN_SKEW_MS;
}

export async function authorizedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const stored = getCloudSession();
  if (stored === null) throw new Error("Not signed in");

  // Keep the stale token when the refresh itself couldn't complete: the
  // request below may still succeed, and a 401 retries the refresh.
  const session = accessTokenStale(stored)
    ? ((await refreshSession()) ?? getCloudSession())
    : stored;
  if (session === null) throw new Error("Session expired");

  const headers = new Headers(init.headers);
  headers.set("authorization", `Bearer ${session.accessToken}`);
  const body = init.body;
  const binaryBody =
    typeof Uint8Array !== "undefined" &&
    (body instanceof Uint8Array ||
      body instanceof ArrayBuffer ||
      (typeof Blob !== "undefined" && body instanceof Blob));
  if (
    headers.has("content-type") === false &&
    body !== null &&
    binaryBody === false
  ) {
    headers.set("content-type", "application/json");
  }

  const res = await fetch(`${cloudApiBaseUrl()}${path}`, { ...init, headers });
  if (res.status !== 401) return res;

  const refreshed = await refreshSession();
  if (refreshed === null) {
    throw new Error(
      getCloudSession() !== null
        ? "Couldn't reach Zephyrwrite Cloud. Check your connection and try again."
        : "Session expired",
    );
  }
  headers.set("authorization", `Bearer ${refreshed.accessToken}`);
  return fetch(`${cloudApiBaseUrl()}${path}`, { ...init, headers });
}

/** One shared refresh. Concurrent callers must not rotate the token twice. */
const refreshInFlight: { current: Promise<CloudSession | null> | null } = {
  current: null,
};

/**
 * Trade the refresh token for a new pair. The cloud rotates the refresh token
 * on every use and kills the old one immediately, so concurrent refreshes would
 * knock each other out. Callers share one attempt.
 */
export function refreshSession(): Promise<CloudSession | null> {
  if (refreshInFlight.current === null) {
    refreshInFlight.current = runRefresh().finally(() => {
      refreshInFlight.current = null;
    });
  }
  return refreshInFlight.current;
}

/**
 * Whether a refusal from the refresh endpoint means the session is really dead.
 * Only a definitive "this token is not valid" answer may sign the writer out.
 * Server errors and gateway hiccups leave the session alone so a cloud deploy
 * doesn't log out every desktop.
 */
function isDefinitiveAuthFailure(status: number): boolean {
  return status === 401 || status === 403;
}

/**
 * The cloud only answers 400 when the request body is wrong, which is our bug,
 * not a dead session. Surface it instead of retrying a broken call forever.
 */
function reportMalformedRefresh(status: number): void {
  if (status !== 400) return;
  console.error(`Refresh request rejected as malformed (${status})`);
  Sentry.captureMessage("Cloud refresh request rejected as malformed", {
    level: "error",
    tags: { feature: "cloud-auth" },
    extra: { status },
  });
}

async function runRefresh(): Promise<CloudSession | null> {
  const session = getCloudSession();
  if (session === null) return null;
  const refreshToken = session.refreshToken;
  if (refreshToken.length === 0) return null;

  const res = await fetchResponse(`${cloudApiBaseUrl()}/v1/auth/refresh`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (res === null) {
    // Offline. The stored session is still the best thing we have.
    return null;
  }

  if (res.ok === false) {
    // Another window may have rotated the token while this request was out.
    // Its result is stored, so adopt that rather than discarding a live session.
    const current = getCloudSession();
    if (current !== null && current.refreshToken !== session.refreshToken)
      return current;
    reportMalformedRefresh(res.status);
    if (isDefinitiveAuthFailure(res.status)) clearCloudSession();
    return null;
  }

  const body = await res.json();
  const userEmail =
    typeof body.user?.email === "string" && body.user.email.length > 0
      ? body.user.email
      : session.email;
  const userId =
    typeof body.user?.id === "string" && body.user.id.length > 0
      ? body.user.id
      : session.userId;
  return saveCloudSession({
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    accessTokenExpiresIn: body.access_token_expires_in,
    email: userEmail,
    userId,
  });
}

export async function fetchEntitlements(): Promise<EntitlementsResponse> {
  const res = await authorizedFetch("/v1/me/entitlements");
  if (res.ok === false)
    throw new Error(`Could not check your account (${res.status}).`);
  const body: unknown = await res.json();
  const decoded = decodeEntitlementsResponse(body);
  if (decoded.ok === false) {
    throw new Error(`Could not check your account (${decoded.error}).`);
  }
  return decoded.value;
}

export function hasActiveEntitlement(
  entitlements: CloudEntitlement[],
  kind: string,
): boolean {
  return entitlements.some((e) => e.kind === kind && e.active);
}

const ENTITLEMENTS_CACHE_MS = 10 * 60 * 1000;

type CachedEntitlements = EntitlementsResponse & { cachedAt: number };

function isCachedEntitlements(value: unknown): value is CachedEntitlements {
  if (value === null || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return Array.isArray(obj.entitlements) && typeof obj.cachedAt === "number";
}

/** Fresh cached entitlements, or `null` when missing/stale/invalid. */
export function readCachedEntitlements(
  maxAgeMs = ENTITLEMENTS_CACHE_MS,
): EntitlementsResponse | null {
  return matchMaybe(readJsonPref("cloudEntitlements", isCachedEntitlements), {
    nothing: () => null,
    just: (cached) => {
      if (Date.now() - cached.cachedAt >= maxAgeMs) return null;
      return {
        entitlements: cached.entitlements.map((e) => ({
          kind: e.kind,
          active: e.active,
          status: e.status ?? (e.active === true ? "active" : "inactive"),
          currentPeriodEnd: e.currentPeriodEnd ?? null,
        })),
        issuedAt: cached.issuedAt,
        graceUntil: cached.graceUntil,
      };
    },
  });
}

export function writeCachedEntitlements(res: EntitlementsResponse): void {
  writeJsonPref("cloudEntitlements", { ...res, cachedAt: Date.now() });
}

export function clearCachedEntitlements(): void {
  removePref("cloudEntitlements");
}

/**
 * A device that paid for cloud backup and has lost its session: work still
 * saves locally, but nothing leaves the machine until the writer signs in
 * again. Worth surfacing, since the failure is otherwise silent.
 *
 * Keyed on the last known entitlements at any age. Signing out clears that
 * cache, so a deliberate sign-out stays quiet.
 */
export function cloudBackupNeedsSignIn(): boolean {
  if (isTauri() === false) return false;
  if (getCloudSession() !== null) return false;
  const lastKnown = readCachedEntitlements(Number.POSITIVE_INFINITY);
  if (lastKnown === null) return false;
  return hasActiveEntitlement(lastKnown.entitlements, "cloud_backup");
}

/** Cache-then-network check for the cloud backup entitlement. */
export async function hasCloudBackupEntitlement(): Promise<boolean> {
  if (getCloudSession() === null) return false;
  try {
    const cached = readCachedEntitlements();
    if (
      cached !== null &&
      hasActiveEntitlement(cached.entitlements, "cloud_backup")
    ) {
      return true;
    }
    const res = await fetchEntitlements();
    writeCachedEntitlements(res);
    return hasActiveEntitlement(res.entitlements, "cloud_backup");
  } catch {
    return false;
  }
}
